import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { createProject, deleteProject, listProjects, reparseEverything } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, RefreshCw, Loader2 } from "lucide-react";

export default function ProjectsList() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.is_super_admin;
  const [projects, setProjects] = useState([]);
  const [otherCompanyCount, setOtherCompanyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", location: "", client_name: "", start_date: "", end_date: "", color: "#F97316" });
  const [submitting, setSubmitting] = useState(false);
  const [reparsingProject, setReparsingProject] = useState(null); // project id currently being reparsed
  const [reparsingAll, setReparsingAll] = useState(false);

  const onReparseProject = async (pid, pname) => {
    if (!window.confirm(t("confirm_reparse_project").replace("{name}", pname))) return;
    setReparsingProject(pid);
    try {
      const r = await reparseEverything({ project_id: pid, only_incomplete: false, max_syncs: 500 });
      const msg = t("reparse_project_done")
        .replace("{ok}", r.succeeded_count)
        .replace("{total}", r.total_processed);
      if (r.failed_count > 0) toast.warning(`${msg} · ${r.failed_count} ${t("failed")}`);
      else toast.success(msg);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setReparsingProject(null); }
  };

  const onReparseAllCompany = async () => {
    const companyName = user?.company_name || "";
    if (!window.confirm(t("confirm_reparse_company").replace("{name}", companyName))) return;
    setReparsingAll(true);
    try {
      const r = await reparseEverything({
        company_id: user?.company_id,
        only_incomplete: false,
        max_syncs: 1000,
      });
      const msg = t("reparse_company_done")
        .replace("{ok}", r.succeeded_count)
        .replace("{total}", r.total_processed);
      if (r.failed_count > 0) toast.warning(`${msg} · ${r.failed_count} ${t("failed")}`);
      else toast.success(msg);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setReparsingAll(false); }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listProjects();
      setProjects(data.projects || []);
      setOtherCompanyCount(data.projects_in_other_companies || 0);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      await createProject(payload);
      toast.success(t("success"));
      setShowCreate(false);
      setForm({ name: "", description: "", location: "", client_name: "", start_date: "", end_date: "", color: "#F97316" });
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSubmitting(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await deleteProject(id);
      toast.success(t("success"));
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div className="px-8 lg:px-12 py-10 max-w-7xl" data-testid="projects-page">
      <header className="flex items-end justify-between gap-3">
        <div>
          <div className="cs-label">portfolio</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-zinc-950 mt-2" data-testid="projects-title">{t("projects_title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && projects.length > 0 && (
            <button
              onClick={onReparseAllCompany}
              disabled={reparsingAll || !!reparsingProject}
              className="inline-flex items-center gap-1.5 border border-zinc-300 text-zinc-700 px-3 py-2 rounded-md text-xs font-semibold hover:bg-zinc-100 disabled:opacity-60"
              title={t("reparse_company_tooltip")}
              data-testid="projects-reparse-all-btn"
            >
              {reparsingAll ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
              {t("reparse_company_btn")}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-cs-accent text-white px-4 py-2.5 rounded-md text-sm font-semibold hover:bg-cs-accentHover" data-testid="projects-create-btn">
              <Plus size={16} /> {t("create_project")}
            </button>
          )}
        </div>
      </header>

      {otherCompanyCount > 0 && (
        <div
          className="mt-4 border border-zinc-200 bg-zinc-50 rounded-md px-4 py-2.5 text-xs text-zinc-600"
          data-testid="projects-other-companies-hint"
        >
          {t("projects_other_companies_hint").replace("{count}", otherCompanyCount)}
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-sm text-zinc-500" data-testid="projects-loading">{t("loading")}</div>
      ) : projects.length === 0 ? (
        <div className="mt-10 border border-dashed border-zinc-300 rounded-md p-12 text-center bg-white" data-testid="projects-empty">
          <p className="text-zinc-500">{t("no_projects")}</p>
        </div>
      ) : (
        <div className="mt-10 bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="cs-label px-5 py-3">{t("project_name")}</th>
                <th className="cs-label px-5 py-3">{t("client")}</th>
                <th className="cs-label px-5 py-3">{t("location")}</th>
                <th className="cs-label px-5 py-3 font-mono">{t("syncs")}</th>
                <th className="cs-label px-5 py-3 font-mono">{t("tasks")}</th>
                <th className="cs-label px-5 py-3 font-mono">{t("status")}</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-t border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`project-row-${p.id}`}>
                  <td className="px-5 py-3">
                    <Link to={`/projects/${p.id}`} className="flex items-center gap-2 font-medium text-zinc-900 hover:text-cs-accent" data-testid={`project-link-${p.id}`}>
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: p.color || "#F97316" }} />
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-5 py-3 text-zinc-600">{p.client_name || "—"}</td>
                  <td className="px-5 py-3 text-zinc-600">{p.location || "—"}</td>
                  <td className="px-5 py-3 font-mono text-zinc-900">{p.stats?.syncs || 0}</td>
                  <td className="px-5 py-3 font-mono text-zinc-900">{p.stats?.tasks || 0}</td>
                  <td className="px-5 py-3"><span className="font-mono text-xs uppercase tracking-wider text-zinc-600">{p.status}</span></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {isAdmin && (
                        <button
                          onClick={() => onReparseProject(p.id, p.name)}
                          disabled={!!reparsingProject || reparsingAll}
                          className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-cs-blueprint disabled:opacity-40"
                          title={t("reparse_project_tooltip")}
                          data-testid={`project-reparse-${p.id}`}
                        >
                          {reparsingProject === p.id ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                        </button>
                      )}
                      {isAdmin && (
                        <button onClick={() => onDelete(p.id)} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-red-600 transition-colors" data-testid={`project-delete-${p.id}`}>
                          <Trash2 size={14} />
                        </button>
                      )}
                      <Link to={`/projects/${p.id}`} className="p-1.5 rounded hover:bg-zinc-200 text-zinc-500 hover:text-zinc-900 transition-colors">
                        <ChevronRight size={16} />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" data-testid="create-project-modal">
          <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h2 className="font-heading text-lg font-semibold text-zinc-950">{t("create_project")}</h2>
            </div>
            <form onSubmit={onCreate} className="p-6 space-y-4">
              {[
                { key: "name", label: t("project_name"), required: true },
                { key: "client_name", label: t("client") },
                { key: "location", label: t("location") },
                { key: "description", label: t("description"), as: "textarea" },
              ].map((f) => (
                <div key={f.key}>
                  <label className="cs-label block mb-1.5">{f.label}{f.required && " *"}</label>
                  {f.as === "textarea" ? (
                    <textarea value={form[f.key]} onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cs-accent focus:border-cs-accent"
                      rows={3} data-testid={`create-${f.key}-input`} />
                  ) : (
                    <input type="text" required={!!f.required} value={form[f.key]} onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cs-accent focus:border-cs-accent" data-testid={`create-${f.key}-input`} />
                  )}
                </div>
              ))}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="cs-label block mb-1.5">{t("start_date")}</label>
                  <input type="date" value={form.start_date} onChange={(e) => setForm((prev) => ({ ...prev, start_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cs-accent" data-testid="create-start-input" />
                </div>
                <div>
                  <label className="cs-label block mb-1.5">{t("end_date")}</label>
                  <input type="date" value={form.end_date} onChange={(e) => setForm((prev) => ({ ...prev, end_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-cs-accent" data-testid="create-end-input" />
                </div>
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="create-cancel-btn">{t("cancel")}</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover disabled:opacity-60" data-testid="create-submit-btn">{submitting ? "..." : t("create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
